import React, { useMemo } from "react";
import {Spin} from "antd";
import {useViewerStore} from "../../../../../../store/store";
import {useOneButtonStore} from "../../../../../../store/oneButtonStore";
import {useCurrentUser} from "../../../../../../contexts/UserContext";
import {resolveEffectiveAnnualUsageKwh} from "../../../../../../helpers/utilityMonthlyDailyToAnnual";
import {
    useProductionPanelLoading,
    useProductionPanelLoadingMessage,
} from "../../../../../../hooks/calculationStateHook";
import { isIpadDevice } from "../../../Three-JS-Viewer/Controllers/Common";
import { TotalSystemEfficiencyProps } from "../../../../../../@types/types";
import ProductionLoader from "./ProductionLoader";

const TotalSystemEfficiency: React.FC<TotalSystemEfficiencyProps> = ({popoutVersion}) => {
    const isPending = useProductionPanelLoading();
    const loadingMessage = useProductionPanelLoadingMessage();
    const { hasCapability } = useCurrentUser();
    const utilityWattage = useViewerStore(state => state.utilityWattage);
    const totalSystemSize = useViewerStore(state => state.totalSystemSize);
    const actualWattage = useViewerStore(state => state.actualWattage);
    const efficiency = useViewerStore(state => state.efficiency);
    const modeCount = useViewerStore(state => state.modeCount);
    const multiplier = useViewerStore(state => state.multiplier);
    const solarAccess = useViewerStore(state => state.weightedSolarAverage);
    const SFannualUsage = useViewerStore(state => state.SFannualUsage);
    const oneButtonAnnualUsageOverride = useViewerStore(state => state.annualUsage);
    const solarUtilityData = useOneButtonStore((s) => s.solarUtilityData);
    const selectedLossesVendor = useViewerStore(state => state.selectedLossesVendor);
    const lossesVendorLabel = selectedLossesVendor === 'OneDraw'
        ? 'oneDRAW'
        : (selectedLossesVendor ?? 'oneDRAW');

    const useOneButtonAnnualInput = hasCapability('Show_OneButton');
    const effectiveAnnualUsage = useMemo(() => {
        const su = solarUtilityData?.SolarUtility as Record<string, unknown> | undefined;
        return resolveEffectiveAnnualUsageKwh({
            useOneButtonAnnualInput,
            oneButtonAnnualUsageOverride,
            solarUtility: su,
            sfAnnualUsage: SFannualUsage,
        });
    }, [useOneButtonAnnualInput, oneButtonAnnualUsageOverride, solarUtilityData, SFannualUsage]);

    const fontSizeStyle = {
        popOutStyle: {
            fontSize: '15px'
        },
        iPadStyle: {
            fontSize: '13px'
        },
        rightHandMenuStyle: {
            fontSize: '13px'
        },
        popOutWidth: {
            width: '300px',
            border: "0px"
        },
        rightHandMenuWidth: {
            width: '230px',
            border: "0px"
        }
    };

    const valueStyle = popoutVersion
        ? isIpadDevice() ? fontSizeStyle.iPadStyle : fontSizeStyle.popOutStyle
        : fontSizeStyle.rightHandMenuStyle;

    const renderCalcValue = (value: React.ReactNode) => (
        <span className="tab__data--calc-value" style={valueStyle}>
            {isPending ? <Spin size="small" /> : value}
        </span>
    );

    if (isPending && popoutVersion) {
        return <ProductionLoader isPopoutVersion={popoutVersion} message={loadingMessage} />;
    }

    const efficiencyBody = (
        <>
            {isPending && loadingMessage && (
                <p className="sideBarLoaderMessage">
                    <Spin size="small" /> {loadingMessage}
                </p>
            )}
            <div className="Thermometer_Production_Container">
                <div id="termometer">
                    {isPending ? (
                        <div className="temperature temperature--loading" data-value="…" style={{ height: '0%' }} />
                    ) : (
                        <div
                            className="temperature"
                            data-value={efficiency + '%'}
                            style={{ height: efficiency + '%' }}
                        />
                    )}
                    <div id="graduations"></div>
                </div>

                <div
                    className="tab__data"
                    style={popoutVersion ? fontSizeStyle.popOutWidth : fontSizeStyle.rightHandMenuWidth}
                >
                    <ul
                        className="tab__data--list"
                        style={{ gap: '3px', margin: '5px 0' }}
                    >
                        <li>
                            <span style={valueStyle}>System Size: </span>
                            {renderCalcValue(
                                `${(Number(totalSystemSize)).toFixed(3)} kW DC`,
                            )}
                        </li>
                        <li>
                            <span style={valueStyle}>Modules:</span>
                            {renderCalcValue(modeCount)}
                        </li>
                        <li>
                            <span style={valueStyle}>Efficiency (TSRF):</span>
                            {renderCalcValue(efficiency)}
                        </li>
                        <li>
                            <span style={valueStyle}>Multiplier:</span>
                            {renderCalcValue(
                                typeof multiplier === 'number' && Number.isFinite(multiplier)
                                    ? Number(multiplier).toFixed(3)
                                    : multiplier,
                            )}
                        </li>
                        <li>
                            <span style={valueStyle}>Losses Vendor:</span>
                            <span className="tab__data--calc-value" style={valueStyle}>{lossesVendorLabel}</span>
                        </li>
                        <li>
                            <span style={valueStyle}>Solar Access:</span>
                            {renderCalcValue(solarAccess)}
                        </li>
                        <li>
                            <span style={valueStyle}>kWh - Utility: </span>
                            {renderCalcValue(Math.round(Number(utilityWattage))?.toLocaleString())}
                        </li>
                        <li>
                            <span style={valueStyle}>kWh - Actual: </span>
                            {renderCalcValue(Math.round(Number(actualWattage))?.toLocaleString())}
                        </li>
                        {effectiveAnnualUsage ? (
                            <>
                                <li>
                                    <span style={valueStyle}>Utility Offset: </span>
                                    {renderCalcValue(
                                        `${Math.round((Number(utilityWattage) / effectiveAnnualUsage) * 100)}%`,
                                    )}
                                </li>
                                <li>
                                    <span style={valueStyle}>Actual Offset: </span>
                                    {renderCalcValue(
                                        `${Math.round((Number(actualWattage) / effectiveAnnualUsage) * 100)}%`,
                                    )}
                                </li>
                                <li>
                                    <span style={valueStyle}>Annual Usage: </span>
                                    <span className="tab__data--calc-value" style={valueStyle}>
                                        {effectiveAnnualUsage.toLocaleString()}
                                    </span>
                                </li>
                            </>
                        ) : null}
                    </ul>
                </div>
            </div>
        </>
    );

    /* Popout (Production panel): match sibling tabs so mobile/tablet can scroll clipped metrics. */
    if (popoutVersion) {
        return (
            <div className="tab-content-container total-system-efficiency-tab">
                <div className="tab-content-scrollable-container">{efficiencyBody}</div>
            </div>
        );
    }

    return efficiencyBody;


}

export default TotalSystemEfficiency;
